import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError, User } from "../../utils";

export default function Voices({
  currentUser,
  getToken,
  eventListener,
  companyId,
}: {
  getToken: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
}) {
  const [init, setInit] = useState(false);
  const [voices, setVoices] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [voiceDialogLoading, setVoiceDialogLoading] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [voiceDescription, setVoiceDescription] = useState("");
  const [voiceFiles, setVoiceFiles] = useState<File[]>([]);
  const [removeBackgroundNoise, setRemoveBackgroundNoise] = useState(true);
  const [testVoiceDialogOpen, setTestVoiceDialogOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<any>({});
  const [testText, setTestText] = useState("");
  const [testAudioUrl, setTestAudioUrl] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVoiceForMenu, setSelectedVoiceForMenu] = useState<any>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteVoice = async (e: any, voiceId: string) => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .delete(`${hostname}/voices/${voiceId}`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        getData();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTestVoice = (voice: any) => {
    setSelectedVoice(voice);
    setTestText("");
    setTestAudioUrl(null);
    setTestVoiceDialogOpen(true);
  };

  const generateTestAudio = async () => {
    if (!testText.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please enter some text to test the voice",
      });
      return;
    }

    const currentToken = await getToken();
    setTestLoading(true);

    try {
      const response = await axios.post(
        `${hostname}/voices/${selectedVoice.id}/test`,
        {
          text: testText,
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
          responseType: "blob",
        },
      );

      // Create a URL for the audio blob
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setTestAudioUrl(audioUrl);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    voice: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedVoiceForMenu(voice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVoiceForMenu(null);
  };

  const handleTestFromMenu = () => {
    if (selectedVoiceForMenu) {
      handleTestVoice(selectedVoiceForMenu);
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedVoiceForMenu) {
      deleteVoice(null, selectedVoiceForMenu.id);
    }
    handleMenuClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const maxFileSize = 11 * 1024 * 1024; // 11MB in bytes

      // Check if any file exceeds 11MB
      const oversizedFiles = files.filter((file) => file.size > maxFileSize);

      if (oversizedFiles.length > 0) {
        eventListener({
          type: "SET_ERROR",
          error: `File size too large: ${oversizedFiles
            .map((f) => f.name)
            .join(", ")}. Maximum file size is 11MB per file.`,
        });
        // Clear the file input
        event.target.value = "";
        return;
      }

      setVoiceFiles(files);
    }
  };

  const createVoice = async () => {
    const currentToken = await getToken();
    setVoiceDialogLoading(true);

    const formData = new FormData();
    formData.append("companyId", companyId);
    formData.append("name", voiceName);
    if (voiceDescription) {
      formData.append("description", voiceDescription);
    }
    formData.append(
      "remove_background_noise",
      removeBackgroundNoise.toString(),
    );

    // Add audio files
    voiceFiles.forEach((file) => {
      formData.append("files", file);
    });

    axios
      .post(`${hostname}/voices`, formData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        getData();
        setVoiceName("");
        setVoiceDescription("");
        setVoiceFiles([]);
        setRemoveBackgroundNoise(true);
        eventListener({
          type: "SET_SUCCESS",
          message: "Voice created successfully",
        });
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setVoiceDialogLoading(false);
        setVoiceDialogOpen(false);
      });
  };

  const getData = async () => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(`${hostname}/voices`, {
        params: {
          companyId,
          page: page + 1,
          pageSize,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setVoices(res.data.items);
        setTotal(res.data.total);
        setInit(true);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();
    }
  }, [currentUser, companyId, page, pageSize]);

  if (loading && !init) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <Box>
      <div style={{ width: "100%", height: 40 }}>
        <Button
          data-tutorial="create-voice"
          variant="contained"
          style={{ marginTop: 10 }}
          startIcon={<AddIcon />}
          onClick={() => setVoiceDialogOpen(true)}
        >
          Create Custom Voice
        </Button>
        <IconButton
          onClick={() => getData()}
          style={{ float: "right", marginTop: 10 }}
        >
          <RefreshOutlined />
        </IconButton>
      </div>
      {voices.length === 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography>
            No custom voices found. Create your first custom voice to get
            started!
          </Typography>
        </Box>
      )}
      <TableContainer data-tutorial="voice-list">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {voices.map((voice) => (
              <TableRow key={voice.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {voice.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {voice.description || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={voice.category || "custom"} size="small" />
                </TableCell>
                <TableCell>
                  {voice.created_date
                    ? new Date(voice.created_date).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, voice)}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog
        open={voiceDialogOpen}
        onClose={() => setVoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create Custom Voice
          <IconButton
            aria-label="close"
            onClick={() => setVoiceDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clone a voice by uploading audio samples. Upload one or more audio
            files (MP3, WAV, etc.) of the voice you want to clone. The audio
            should be clear and at least 30 seconds long for best results.
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 1, fontSize: "0.875rem", color: "text.secondary" }}
          >
            • Maximum file size: 11MB per file
            <br />
            • Recommended: Clear audio with minimal background noise
            <br />• Total audio duration: 30 seconds - 5 minutes
          </DialogContentText>
          <TextField
            style={{ marginTop: 20 }}
            label="Voice Name"
            value={voiceName}
            fullWidth
            required
            onChange={(e: any) => setVoiceName(e.target.value)}
            helperText="Give your voice a descriptive name"
          />
          <TextField
            style={{ marginTop: 20 }}
            label="Description"
            value={voiceDescription}
            fullWidth
            multiline
            rows={2}
            onChange={(e: any) => setVoiceDescription(e.target.value)}
            helperText="Optional: Describe the characteristics of this voice"
          />
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" component="label" fullWidth>
              Upload Audio Files
              <input
                type="file"
                hidden
                multiple
                accept="audio/*"
                onChange={handleFileChange}
              />
            </Button>
            {voiceFiles.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {voiceFiles.length} file(s) selected:
                </Typography>
                {voiceFiles.map((file, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    • {file.name}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={removeBackgroundNoise}
                onChange={(e) => setRemoveBackgroundNoise(e.target.checked)}
              />
            }
            label="Remove background noise from audio"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoiceDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={createVoice}
            loading={voiceDialogLoading}
            disabled={!voiceName || voiceFiles.length === 0}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="sm"
        open={testVoiceDialogOpen}
        onClose={() => setTestVoiceDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>
          Test Voice: {selectedVoice.name}
          <IconButton
            aria-label="close"
            onClick={() => setTestVoiceDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter some text to generate audio with this voice and hear how it
            sounds.
          </DialogContentText>
          <TextField
            style={{ marginTop: 20 }}
            label="Test Text"
            value={testText}
            fullWidth
            multiline
            rows={4}
            onChange={(e: any) => setTestText(e.target.value)}
            placeholder="Enter text to convert to speech..."
            helperText="Enter the text you want to hear in this voice"
          />
          {testAudioUrl && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Generated Audio:
              </Typography>
              <audio controls style={{ width: "100%" }}>
                <source src={testAudioUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestVoiceDialogOpen(false)}>Close</Button>
          <LoadingButton
            variant="contained"
            onClick={generateTestAudio}
            loading={testLoading}
            disabled={!testText.trim()}
          >
            Generate Audio
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleTestFromMenu}>
          <ListItemIcon>
            <PlayArrowOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Test</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
